import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Phone, Users, Calendar, Clock, TrendingUp, AlertTriangle } from 'lucide-react';

export default function AnalyticsPage() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const business = user?.business;

  useEffect(() => {
    const fetch = async () => { try { const { data } = await api.get('/dashboard/stats'); setStats(data); } catch {} };
    fetch();
  }, [api]);

  const minutesPercent = stats ? Math.round((stats.minutes_used / stats.minutes_cap) * 100) : 0;
  const smsPercent = stats ? Math.round((stats.sms_used / (stats.sms_cap || 100)) * 100) : 0;

  return (
    <div data-testid="analytics-page" className="space-y-6">
      <h1 className="font-heading text-2xl font-semibold text-gray-900 dark:text-white">Analytics</h1>

      {/* Usage Alerts */}
      {minutesPercent >= 80 && (
        <div className={`flex items-center gap-3 p-4 rounded-xl ${minutesPercent >= 100 ? 'bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30' : 'bg-amber-50 dark:bg-amber-500/15 border border-amber-200 dark:border-amber-500/40'}`}>
          <AlertTriangle className={`w-5 h-5 ${minutesPercent >= 100 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} />
          <p className={`text-sm font-medium ${minutesPercent >= 100 ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
            {minutesPercent >= 100 ? "You've reached your monthly minute limit, upgrade to avoid disruption" : `You've used ${minutesPercent}% of your monthly minutes`}
          </p>
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI label="Total Calls" value={stats?.calls_count || 0} icon={Phone} />
        <KPI label="Minutes Used" value={`${stats?.minutes_used || 0}/${stats?.minutes_cap || 600}`} icon={Clock} />
        <KPI label="Leads Created" value={stats?.leads_count || 0} icon={Users} />
        <KPI label="Appointments" value={stats?.appointments_count || 0} icon={Calendar} />
        <KPI label="Conversion Rate" value={stats?.leads_count ? `${Math.round((stats.appointments_count / stats.leads_count) * 100)}%` : '0%'} icon={TrendingUp} />
        <KPI label="SMS Sent" value={`${business?.sms_used || 0}/${business?.sms_cap || 100}`} icon={Phone} />
      </div>

      {/* Usage Charts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Minutes Usage */}
        <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
          <h3 className="font-heading text-base font-medium text-gray-900 dark:text-white mb-4">Minutes Usage</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-slate-300">Used</span>
                <span className="text-gray-900 dark:text-white font-medium">{stats?.minutes_used || 0} min</span>
              </div>
              <div className="w-full h-4 bg-gray-100 dark:bg-white/[0.05] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${minutesPercent >= 95 ? 'bg-red-500' : minutesPercent >= 80 ? 'bg-amber-500' : 'bg-blue-600'}`}
                  style={{ width: `${Math.min(minutesPercent, 100)}%` }} />
              </div>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{minutesPercent}% of {stats?.minutes_cap || 600} min cap</p>
            </div>
            <div className="pt-3 border-t border-gray-100 dark:border-white/10 grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{stats?.minutes_used || 0}</p><p className="text-xs text-gray-400 dark:text-slate-500">Used</p></div>
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{(stats?.minutes_cap || 600) - (stats?.minutes_used || 0)}</p><p className="text-xs text-gray-400 dark:text-slate-500">Remaining</p></div>
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{stats?.minutes_cap || 600}</p><p className="text-xs text-gray-400 dark:text-slate-500">Total Cap</p></div>
            </div>
          </div>
        </div>

        {/* SMS Usage */}
        <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
          <h3 className="font-heading text-base font-medium text-gray-900 dark:text-white mb-4">SMS Usage</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-slate-300">Sent</span>
                <span className="text-gray-900 dark:text-white font-medium">{business?.sms_used || 0} msgs</span>
              </div>
              <div className="w-full h-4 bg-gray-100 dark:bg-white/[0.05] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${smsPercent >= 95 ? 'bg-red-500' : smsPercent >= 80 ? 'bg-amber-500' : 'bg-blue-600'}`}
                  style={{ width: `${Math.min(smsPercent, 100)}%` }} />
              </div>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{smsPercent}% of {business?.sms_cap || 100} SMS cap</p>
            </div>
            <div className="pt-3 border-t border-gray-100 dark:border-white/10 grid grid-cols-3 gap-4 text-center">
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{business?.sms_used || 0}</p><p className="text-xs text-gray-400 dark:text-slate-500">Sent</p></div>
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{(business?.sms_cap || 100) - (business?.sms_used || 0)}</p><p className="text-xs text-gray-400 dark:text-slate-500">Remaining</p></div>
              <div><p className="text-xl font-heading font-semibold text-gray-900 dark:text-white">{business?.sms_cap || 100}</p><p className="text-xs text-gray-400 dark:text-slate-500">Total Cap</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* Call Activity (simulated chart) */}
      <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-base font-medium text-gray-900 dark:text-white mb-4">Call Activity (Last 7 Days)</h3>
        <div className="flex items-end gap-2 h-40">
          {[3, 5, 2, 7, 4, 6, 3].map((val, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-blue-600 rounded-t-lg transition-all" style={{ height: `${(val / 7) * 100}%` }} />
              <span className="text-[10px] text-gray-400 dark:text-slate-500">{['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Status Breakdown */}
      <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-base font-medium text-gray-900 dark:text-white mb-4">Lead Status Breakdown</h3>
        <div className="grid grid-cols-5 gap-3">
          {[
            { status: 'New', color: 'bg-blue-500', count: 2 },
            { status: 'Contacted', color: 'bg-amber-500', count: 1 },
            { status: 'Qualified', color: 'bg-purple-500', count: 1 },
            { status: 'Booked', color: 'bg-emerald-500', count: 1 },
            { status: 'Lost', color: 'bg-red-400', count: 0 },
          ].map((item, i) => (
            <div key={i} className="text-center">
              <div className={`w-full h-2 ${item.color} rounded-full mb-2`} />
              <p className="text-lg font-heading font-semibold text-gray-900 dark:text-white">{item.count}</p>
              <p className="text-[10px] text-gray-400 dark:text-slate-500">{item.status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon }) {
  return (
    <div className="bg-white dark:bg-white/[0.04] dark:backdrop-blur-xl border border-gray-200 dark:border-white/10 rounded-xl p-4">
      <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400 mb-2" />
      <p className="text-lg font-heading font-semibold text-gray-900 dark:text-white">{value}</p>
      <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}
